import { FormEvent, useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { authClient } from '../../../lib/auth-client'
import { toast } from 'react-toastify'
import { CheckCircle2 } from 'lucide-react'

export const ResetPasswordRoute = () => {
    const [searchParams] = useSearchParams()
    const token = searchParams.get('token')

    const [password, setPassword] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')
    const [loading, setLoading] = useState(false)
    const [success, setSuccess] = useState(false)

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (password !== confirmPassword) {
            toast.error('Passwords do not match')
            return
        }

        if (password.length < 8) {
            toast.error('Password must be at least 8 characters')
            return
        }

        if (!token) {
            toast.error('Invalid or missing reset token')
            return
        }

        setLoading(true)

        const { error } = await authClient.resetPassword({
            newPassword: password,
            token,
        })

        if (error) {
            toast.error(error.message || 'An error occurred')
            setLoading(false)
            return
        }

        setSuccess(true)
        setLoading(false)
    }

    return (
        <div className="flex min-h-screen">
            <div className="hidden lg:flex lg:w-1/2 bg-foreground items-center justify-center p-12">
                <div className="max-w-md">
                    <h2 className="text-3xl font-semibold tracking-tight text-background">
                        Trakkrr
                    </h2>
                    <p className="text-background/60 mt-3 text-sm leading-relaxed">
                        Monitor your fleet, manage devices, and keep operations
                        running with real-time insights.
                    </p>
                </div>
            </div>

            <div className="flex flex-1 flex-col justify-center px-6 sm:px-12 lg:px-16 xl:px-24 bg-background">
                <div className="w-full max-w-sm mx-auto">
                    {success ? (
                        <div>
                            <div className="h-10 w-10 rounded-full bg-green-50 border border-green-200 flex items-center justify-center mb-4">
                                <CheckCircle2 className="h-5 w-5 text-green-600" />
                            </div>
                            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                                Password reset
                            </h1>
                            <p className="text-sm text-muted-foreground mt-1.5">
                                Your password has been reset successfully.
                            </p>
                            <Link
                                to="/login"
                                className="inline-flex mt-6 h-10 items-center justify-center rounded-md bg-foreground text-background text-sm font-medium px-6 hover:bg-foreground/90 transition-colors"
                            >
                                Sign in
                            </Link>
                        </div>
                    ) : !token ? (
                        <div>
                            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                                Invalid link
                            </h1>
                            <p className="text-sm text-muted-foreground mt-1.5">
                                This reset link is invalid or has expired.
                                Please request a new one.
                            </p>
                            <Link
                                to="/forgot-password"
                                className="inline-block mt-6 text-xs text-muted-foreground hover:text-foreground transition-colors"
                            >
                                Request new link
                            </Link>
                        </div>
                    ) : (
                        <>
                            <div className="mb-8">
                                <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                                    Reset password
                                </h1>
                                <p className="text-sm text-muted-foreground mt-1.5">
                                    Enter your new password below.
                                </p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="space-y-1.5">
                                    <label
                                        htmlFor="password"
                                        className="text-xs font-medium text-muted-foreground uppercase tracking-wide"
                                    >
                                        New password
                                    </label>
                                    <input
                                        type="password"
                                        id="password"
                                        value={password}
                                        onChange={(e) =>
                                            setPassword(e.target.value)
                                        }
                                        required
                                        minLength={8}
                                        autoComplete="new-password"
                                        className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-colors"
                                        placeholder="Min. 8 characters"
                                    />
                                </div>
                                <div className="space-y-1.5">
                                    <label
                                        htmlFor="confirmPassword"
                                        className="text-xs font-medium text-muted-foreground uppercase tracking-wide"
                                    >
                                        Confirm password
                                    </label>
                                    <input
                                        type="password"
                                        id="confirmPassword"
                                        value={confirmPassword}
                                        onChange={(e) =>
                                            setConfirmPassword(e.target.value)
                                        }
                                        required
                                        minLength={8}
                                        autoComplete="new-password"
                                        className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring transition-colors"
                                        placeholder="Re-enter your password"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="h-10 w-full rounded-md bg-foreground text-background text-sm font-medium hover:bg-foreground/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:pointer-events-none"
                                >
                                    {loading
                                        ? 'Resetting...'
                                        : 'Reset password'}
                                </button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}
